import PropTypes from "prop-types";
import { useContext, useEffect, useRef } from "react";
import { TranslationContext } from "../contexts";

export function DropDownMenu({
  items = [],
  MenuButton = null,
  showMenu,
  setShowMenu,
  lastOptionLabel = "",
  lastOptionFn = null,
  noScaleOnHover = false,
  menuItemsPosition = "center",
  textColor = "black",
  data_testid = "",
  onItemClicked = () => {},
}) {
  const menuRef = useRef(null);
  const menuTitleRef = useRef(null);

  const { t } = useContext(TranslationContext);

  // Determine menu items list positioning classes
  const getMenuItemsPositionClasses = () => {
    const baseClasses =
      "absolute top-10 -translate-x-1/2 bg-white z-[100] p-0 grid justify-items-center w-max transition-all duration-300 ease-in-out";

    let leftClass = "";
    if (menuItemsPosition === "left") {
      leftClass = "left-[-50%]";
    } else if (menuItemsPosition === "center") {
      leftClass = "left-1/2";
    } else {
      leftClass = "left-full";
    }

    const heightClass = showMenu ? "max-h-max" : "max-h-0";
    const shadowClass = showMenu
      ? "shadow-[0_4px_8px_4px_rgba(0,0,0,0.2)]"
      : "shadow-none";

    return `${baseClasses} ${leftClass} ${heightClass} ${shadowClass}`;
  };

  function handleItemClick(input) {
    setShowMenu(false);
    onItemClicked(input);
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !menuTitleRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  return (
    <div className="hidden xl:block relative w-max" data-testid={data_testid}>
      <div className="grid">
        <button
          type="button"
          className={`font-medium text-base cursor-pointer border-none p-0 bg-transparent transition-all duration-200 ease-in-out hover:text-amber-500 ${
            !noScaleOnHover ? "hover:scale-110" : ""
          }`}
          style={{ color: textColor }}
          ref={menuTitleRef}
          onClick={() => setShowMenu((prev) => !prev)}
        >
          {MenuButton ?? t("menu")}
        </button>
      </div>

      <div className={getMenuItemsPositionClasses()} ref={menuRef}>
        {items.map((item) => (
          <button
            type="button"
            key={item.arg}
            className={`transition-opacity duration-300 ease-in-out cursor-pointer box-border text-center inline-block font-medium w-full border-none bg-transparent break-words whitespace-pre-wrap hover:bg-black/10 focus:bg-black/10 ${
              showMenu
                ? "opacity-100 visible py-2.5 px-4"
                : "opacity-0 invisible p-0"
            }`}
            onClick={() => {
              handleItemClick(item.arg);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleItemClick(item.arg)}
          >
            {typeof item.title === "string" ? t(item.title) : item.title}
          </button>
        ))}
        <hr
          className={`m-0 border-b border-black/10 w-full ${
            showMenu ? "visible" : "invisible"
          }`}
        />
        {lastOptionLabel && lastOptionFn && (
          <button
            className={`transition-opacity duration-300 ease-in-out cursor-pointer box-border text-center inline-block font-medium w-full border-none bg-transparent break-words whitespace-pre-wrap hover:bg-black/10 focus:bg-black/10 ${
              showMenu
                ? "opacity-100 visible py-2.5 px-4"
                : "opacity-0 invisible p-0"
            }`}
            onClick={() => {
              lastOptionFn();
            }}
            type="button"
          >
            {lastOptionLabel}
          </button>
        )}
      </div>
    </div>
  );
}

DropDownMenu.propTypes = {
  items: PropTypes.array.isRequired,
  MenuButton: PropTypes.element,
  showMenu: PropTypes.bool.isRequired,
  setShowMenu: PropTypes.func.isRequired,
  lastOptionLabel: PropTypes.string,
  lastOptionFn: PropTypes.func,
  onItemClicked: PropTypes.func,
  noScaleOnHover: PropTypes.bool,
  menuItemsPosition: PropTypes.oneOf(["left", "center", "right"]),
  menuItemsWidth: PropTypes.string,
  textColor: PropTypes.string,
  data_testid: PropTypes.string,
};
